import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "wager-card" }
const _hoisted_2 = { class: "card-meta-row" }
const _hoisted_3 = { class: "meta-date" }
const _hoisted_4 = { class: "meta-id" }
const _hoisted_5 = { class: "meta-id-text" }
const _hoisted_6 = ["aria-label"]
const _hoisted_7 = { class: "card-title-row" }
const _hoisted_8 = { class: "game-name" }
const _hoisted_9 = { class: "card-details" }
const _hoisted_10 = { class: "detail-row" }
const _hoisted_11 = { class: "detail-label" }
const _hoisted_12 = { class: "detail-value" }
const _hoisted_13 = { class: "detail-row" }
const _hoisted_14 = { class: "detail-label" }
const _hoisted_15 = { class: "detail-value" }
const _hoisted_16 = { class: "detail-row" }
const _hoisted_17 = { class: "detail-label" }
const _hoisted_18 = { class: "detail-value" }
const _hoisted_19 = { class: "detail-row" }
const _hoisted_20 = { class: "detail-label" }
const _hoisted_21 = { class: "detail-value" }
const _hoisted_22 = { class: "card-action" }

import { computed } from 'vue'

import { useI18n } from 'vue-i18n'
import type { Transaction } from '@/stores/transactions'
import { formatCurrency } from '@/utils/currency'
import { useTheme } from '@/composables/useTheme'
import { copyTextToClipboard } from '@/utils/copyToClipboard'


const __default__ = {
  name: 'WagerCard',
}


export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
    transaction: {}
  },
  emits: ["view-details", "rebet", "copy-success", "copy-failed"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { t } = useI18n()
const { currentThemeName } = useTheme()
const useWonLostLabels = computed(() => currentThemeName.value === 'wsb')

const displayGameName = computed(() => {
  const name = props.transaction.game_name?.trim()
  return name || '—'
})

const drawsPlayedDisplay = computed(() => {
  const count = props.transaction.draws_played
  if (count === undefined || count === null) {
    return '—'
  }
  return count
})

const formatStatusLabel = (status: string, winnings: number | string = 0) => {
  if (!useWonLostLabels.value || String(status).toLowerCase() !== 'settled') {
    return status
  }
  return parseFloat(String(winnings || 0)) > 0 ? t('wagers.won') : t('wagers.lost')
}

const formatStatusClass = (status: string, winnings: number | string = 0) => {
  if (!useWonLostLabels.value || String(status).toLowerCase() !== 'settled') {
    return String(status).toLowerCase()
  }
  return parseFloat(String(winnings || 0)) > 0 ? 'won' : 'lost'
}

const handleViewDetails = (event: Event) => {
  event.preventDefault()
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.blur()
  }
  emit('view-details', props.transaction)
}

const formatDateReadable = (dateString: string) => {
  const date = new Date(dateString)
  const day = date.getDate().toString().padStart(2, '0')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')

  return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds}`
}

const getTotalReturn = (transaction: Transaction) => {
  if (transaction.bet_status.toLowerCase() === 'open') {
    return t('wagers.tbc')
  }
  return formatCurrency(transaction.total_winnings, transaction.currency_symbol)
}

const copyToClipboard = async (text: string) => {
  const copied = await copyTextToClipboard(text)
  if (copied) {
    emit('copy-success')
  } else {
    emit('copy-failed')
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, _toDisplayString(formatDateReadable(_ctx.transaction.created_at)), 1),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t('wagers.idShort')) + ": " + _toDisplayString(_ctx.transaction.id), 1),
        _createElementVNode("button", {
          type: "button",
          class: "copy-button",
          "aria-label": _ctx.$t('transactionDetails.copyToClipboard'),
          onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (copyToClipboard(String(_ctx.transaction.id))), ["stop"]))
        }, _cache[1] || (_cache[1] = [
          _createElementVNode("svg", {
            width: "16",
            height: "16",
            viewBox: "0 0 16 16",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            "aria-hidden": "true"
          }, [
            _createElementVNode("rect", {
              x: "5",
              y: "5",
              width: "9",
              height: "9",
              rx: "1",
              stroke: "currentColor",
              "stroke-width": "1.25"
            }),
            _createElementVNode("path", {
              d: "M4 11H3C2.44772 11 2 10.5523 2 10V3C2 2.44772 2.44772 2 3 2H10C10.5523 2 11 2.44772 11 3V4",
              stroke: "currentColor",
              "stroke-width": "1.25"
            })
          ], -1)
        ]), 8, _hoisted_6)
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("span", _hoisted_8, _toDisplayString(displayGameName.value), 1),
      _createElementVNode("span", {
        class: _normalizeClass(['status-value', formatStatusClass(_ctx.transaction.bet_status, _ctx.transaction.total_winnings)])
      }, _toDisplayString(formatStatusLabel(_ctx.transaction.bet_status, _ctx.transaction.total_winnings)), 3)
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("span", _hoisted_11, _toDisplayString(_ctx.$t('wagers.description')) + ":", 1),
        _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.$t('wagers.wager')), 1)
      ]),
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("span", _hoisted_14, _toDisplayString(_ctx.$t('wagers.drawsPlayed')) + ":", 1),
        _createElementVNode("span", _hoisted_15, _toDisplayString(drawsPlayedDisplay.value), 1)
      ]),
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("span", _hoisted_17, _toDisplayString(_ctx.$t('wagers.totalBetAmount')), 1),
        _createElementVNode("span", _hoisted_18, _toDisplayString(_unref(formatCurrency)(_ctx.transaction.total, _ctx.transaction.currency_symbol)), 1)
      ]),
      _createElementVNode("div", _hoisted_19, [
        _createElementVNode("span", _hoisted_20, _toDisplayString(_ctx.$t('wagers.totalReturn')) + ":", 1),
        _createElementVNode("span", _hoisted_21, _toDisplayString(getTotalReturn(_ctx.transaction)), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_22, [
      _createElementVNode("button", {
        type: "button",
        class: "view-details-btn",
        onClick: handleViewDetails
      }, _toDisplayString(_ctx.$t('wagers.viewDetails')), 1)
    ])
  ]))
}
}

})